import { type RecordEventRaw } from 'components/framework/models/generated/types';
import Effort, { type Performance } from './Effort';
import { clamp } from 'lodash';

export default class TeamPerformance implements Performance {
  committed = new Effort('committed');
  added = new Effort('added');
  removed = new Effort('removed');
  completed = new Effort('completed');
  incomplete = new Effort('incomplete');

  /**
   * Records that have been in this iteration
   */
  recordsAssigned: Set<string> = new Set();

  addRecord(recordEvent: RecordEventRaw) {
    this.recordsAssigned.add(
      [recordEvent.eventSubjectType, recordEvent.eventSubjectId].join('-')
    );
  }

  recordAlreadySeen(recordEvent: RecordEventRaw) {
    return this.recordsAssigned.has(
      [recordEvent.eventSubjectType, recordEvent.eventSubjectId].join('-')
    );
  }

  /**
   * Return a cloned version of this object with the values of the other object
   * subtracted. This gives a delta of the two performances.
   */
  toSubstracted(other: TeamPerformance): TeamPerformance {
    const subtracted = this.clone();

    ['committed', 'added', 'removed', 'completed', 'incomplete'].forEach(
      field => {
        subtracted[field].subtract(other[field]);
      }
    );
    return subtracted;
  }

  get completedPercent() {
    const effort = new Effort('completedPercent');

    ['count', 'originalEstimate', 'remainingEstimate'].forEach(field => {
      effort[field] =
        this.completed[field] + this.incomplete[field] === 0
          ? 0
          : clamp(
              Math.round(
                (this.completed[field] /
                  (this.completed[field] + this.incomplete[field])) *
                  100
              ),
              0,
              100
            );
    });

    return effort;
  }

  /**
   * Create a deep clone of this object
   */
  clone(): TeamPerformance {
    const clone = new TeamPerformance();
    clone.recordsAssigned = new Set(this.recordsAssigned);

    ['committed', 'added', 'removed', 'completed', 'incomplete'].forEach(
      field => {
        clone[field] = this[field].clone();
      }
    );

    return clone;
  }
}
